import type {
  GraphPathLookupUnicastParameters,
  GraphPathLookupUnicastParametersBase,
} from '../api/GraphPathLookupParameters';
import type { PathLookupAdvancedOptionsBase } from '../graphSettings/GraphAdvancedOptions';
import type { PathLookupIpProtocol } from '../pathLookup/PacketHeader';

/**
 * The subset of an AIM flow needed to synthesize a unicast path-lookup request.
 * The `aimFlow` model row and the Flows-table row both satisfy this shape.
 */
export type AimFlowTuple = {
  srcIp: string | null;
  dstIp: string | null;
  protocol: number | null;
  srcPort: number | null;
  dstPort: number | null;
};

/** IANA protocol number (`aimFlow.protocol`) → path-lookup protocol. */
const IANA_PROTOCOL_TO_PATH_LOOKUP: Readonly<
  Record<number, PathLookupIpProtocol>
> = {
  1: 'icmp',
  6: 'tcp',
  17: 'udp',
  58: 'icmpv6',
};

/** Full port range used when a flow leaves the port unspecified (NULL). */
const DEFAULT_PORT_RANGE = '1024-65535';

/** Packet TTL for the synthesized lookup (matches the on-demand path-lookup). */
const DEFAULT_TTL = 64;

const portToString = (port: number | null): string =>
  port === null ? DEFAULT_PORT_RANGE : String(port);

/**
 * Map an AIM flow's 5-tuple onto a unicast path-lookup request. Returns `null`
 * for flows that cannot form a unicast lookup (missing endpoint / protocol, or
 * a protocol the engine does not model).
 *
 * Shared by the backend calculation job and the frontend "open path-lookup"
 * action so both drive the exact same lookup for a given flow.
 */
export function aimFlowTupleToPathLookupParameters(
  flow: AimFlowTuple,
): GraphPathLookupUnicastParameters | null {
  const { srcIp, dstIp, protocol: ianaProtocol } = flow;
  if (srcIp === null || dstIp === null || ianaProtocol === null) {
    return null;
  }
  const protocol = IANA_PROTOCOL_TO_PATH_LOOKUP[ianaProtocol];
  if (protocol === undefined) {
    return null;
  }

  const common: GraphPathLookupUnicastParametersBase &
    Omit<PathLookupAdvancedOptionsBase, 'protocol'> = {
    type: 'pathLookup',
    pathLookupType: 'unicast',
    groupBy: 'siteName',
    firstHopAlgorithm: { type: 'automatic' },
    securedPath: false,
    networkMode: false,
    startingPoint: srcIp,
    destinationPoint: dstIp,
    enableRegions: false,
    srcRegions: '.*',
    dstRegions: '.*',
    ttl: DEFAULT_TTL,
    fragmentOffset: 0,
    otherOptions: {
      applications: '',
      applicationsMatchWithRegex: true,
      category: '',
      tracked: false,
      url: '',
    },
  };

  switch (protocol) {
    case 'tcp':
      return {
        ...common,
        protocol: 'tcp',
        l4Options: {
          srcPorts: portToString(flow.srcPort),
          dstPorts: portToString(flow.dstPort),
          flags: [],
        },
      };
    case 'udp':
      return {
        ...common,
        protocol: 'udp',
        l4Options: {
          srcPorts: portToString(flow.srcPort),
          dstPorts: portToString(flow.dstPort),
        },
      };
    default:
      // icmp / icmpv6 — default to an echo request (type/code are not part of
      // the AIM 5-tuple); `protocol` narrows to 'icmp' | 'icmpv6' here.
      return {
        ...common,
        protocol,
        l4Options: { type: protocol === 'icmp' ? 8 : 128, code: 0 },
      };
  }
}
